/**
 * Blog Hub Page
 * SEO-optimized content hub for TENSION
 */

import { useState } from 'react';
import { useNavigate } from 'react-router';
import {
  useSEO,
  useStructuredData,
  COMPETITIVE_KEYWORD_CLUSTERS,
  mergeKeywordSets,
  SITE_LOGO_ABSOLUTE_URL,
} from '../lib/seo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import {
  Search,
  Clock,
  ArrowRight,
  Heart,
  Brain,
  MessageCircle,
  TrendingUp,
  Users,
  Sparkles,
} from 'lucide-react';
import { StockImage, WarmMarketingBackdrop } from '../components/marketing/MarketingVisuals';
import { MarketingSiteHeader } from '../components/marketing/MarketingSiteHeader';

interface BlogPost {
  slug: string;
  title: string;
  description: string;
  author: string;
  publishedAt: string;
  readTime: string;
  category: string;
  tags: string[];
  featured: boolean;
  image?: string;
}

// Blog posts database - Add more for better SEO
const blogPosts: BlogPost[] = [
  {
    slug: 'relationship-anxiety-guide',
    title: 'The Complete Guide to Relationship Anxiety: Signs, Causes & Solutions',
    description: 'Learn to identify and manage relationship anxiety with science-backed strategies. Understand attachment theory, recognize warning signs, and build healthier connections.',
    author: 'Dr. Sarah Chen',
    publishedAt: '2026-03-10',
    readTime: '12 min',
    category: 'Relationship Psychology',
    tags: ['relationship anxiety', 'attachment theory', 'mental health', 'self-awareness'],
    featured: true,
  },
  {
    slug: 'attachment-theory-explained',
    title: 'Attachment Theory in Relationships: How Your Style Affects Your Love Life',
    description: 'Discover how your attachment style shapes your relationships. Learn about anxious, avoidant, and secure attachment patterns and how to develop healthier bonds.',
    author: 'Dr. Michael Torres',
    publishedAt: '2026-03-08',
    readTime: '10 min',
    category: 'Attachment Theory',
    tags: ['attachment theory', 'anxious attachment', 'avoidant attachment', 'secure attachment'],
    featured: true,
  },
  {
    slug: 'how-to-reduce-relationship-tension',
    title: '7 Proven Ways to Reduce Relationship Tension Before It Escalates',
    description: 'Practical, science-backed techniques to identify and resolve relationship tension early. Learn communication strategies, de-escalation tactics, and preventive measures.',
    author: 'Jessica Williams, LMFT',
    publishedAt: '2026-03-05',
    readTime: '8 min',
    category: 'Communication',
    tags: ['conflict resolution', 'communication skills', 'relationship tips', 'tension reduction'],
    featured: true,
  },
  {
    slug: 'emotional-distance-warning-signs',
    title: '12 Warning Signs of Emotional Distance in Relationships',
    description: 'Recognize the early warning signs of emotional distance before it damages your relationship. Learn what causes disconnection and how to rebuild intimacy.',
    author: 'Dr. Sarah Chen',
    publishedAt: '2026-03-03',
    readTime: '9 min',
    category: 'Relationship Health',
    tags: ['emotional distance', 'intimacy issues', 'relationship problems', 'connection'],
    featured: false,
  },
  {
    slug: 'dating-anxiety-tips',
    title: 'Connection Anxiety: 10 Expert Tips to Feel More Grounded & Present',
    description: 'Reduce connection anxiety with proven strategies from relationship experts. Build confidence, stay present, and create healthy interactions.',
    author: 'Marcus Johnson, PhD',
    publishedAt: '2026-03-01',
    readTime: '7 min',
    category: 'Relationship Readiness',
    tags: ['connection anxiety', 'relationship readiness', 'confidence', 'healthy communication'],
    featured: false,
  },
  {
    slug: 'communication-patterns-that-predict-breakups',
    title: 'The 4 Communication Patterns That Predict Relationship Failure',
    description: 'Research shows these communication patterns are strong predictors of breakups. Learn to recognize and fix them before it\'s too late.',
    author: 'Dr. Michael Torres',
    publishedAt: '2026-02-28',
    readTime: '11 min',
    category: 'Communication',
    tags: ['communication', 'relationship research', 'conflict', 'Gottman method'],
    featured: false,
  },
];

export default function Blog() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // SEO Configuration
  useSEO({
    title: 'Relationship App Advice and Couples Communication Guides | TENSION Blog',
    description:
      'Expert relationship app guidance on communication, conflict prevention, and emotional connection for couples.',
    keywords: mergeKeywordSets(
      COMPETITIVE_KEYWORD_CLUSTERS.core as unknown as string[],
      COMPETITIVE_KEYWORD_CLUSTERS.communication as unknown as string[],
      COMPETITIVE_KEYWORD_CLUSTERS.conflict as unknown as string[],
      ['relationship app advice', 'couples communication guides', 'apps like paired'],
    ),
    canonical: '/blog',
    ogType: 'website',
    twitterCard: 'summary_large_image',
  });

  // Structured Data for Blog
  useStructuredData({
    type: 'Organization',
    data: {
      name: 'TENSION Blog',
      description: 'Expert relationship advice and anxiety management tips',
      url: 'https://tensionlabs.net/blog',
      logo: SITE_LOGO_ABSOLUTE_URL,
      sameAs: [
        'https://twitter.com/tensionapp',
        'https://instagram.com/tensionapp',
      ],
    },
  });

  // Get unique categories
  const categories = Array.from(new Set(blogPosts.map(post => post.category)));

  // Filter posts
  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = searchQuery === '' || 
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesCategory = !selectedCategory || post.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  const featuredPosts = filteredPosts.filter(post => post.featured);
  const regularPosts = filteredPosts.filter(post => !post.featured);

  const getCategoryIcon = (category: string) => {
    const icons: Record<string, any> = {
      'Relationship Psychology': Brain,
      'Attachment Theory': Heart,
      'Communication': MessageCircle,
      'Relationship Health': TrendingUp,
      'Relationship Readiness': Users,
    };
    return icons[category] || Sparkles;
  };

  return (
    <WarmMarketingBackdrop>
    <div className="min-h-screen">
      <MarketingSiteHeader variant="hub" activeNav="blog" />

      {/* Hero Section */}
      <section className="px-4 pb-4 pt-8 md:pt-12">
        <div className="marketing-panel mx-auto grid max-w-6xl gap-8 md:grid-cols-[1.05fr_0.95fr] md:items-stretch">
          <div className="flex flex-col justify-center p-8 text-center md:p-12 md:text-left">
            <h1 className="text-4xl font-black tracking-tight text-neutral-900 md:text-5xl">
              Relationship Advice & Psychology Insights
            </h1>
            <p className="mt-4 text-lg text-neutral-600 md:text-xl">
              Expert guidance on managing relationship anxiety, understanding attachment theory, and building healthier
              connections.
            </p>
          </div>
          <div className="relative min-h-[220px]">
            <StockImage
              stockKey="handsWalking"
              width={900}
              height={700}
              priority
              alt="Couple walking together with confidence and emotional connection"
              className="h-full min-h-[220px] w-full object-cover md:rounded-none md:rounded-r-3xl"
            />
            <div
              className="pointer-events-none absolute inset-0 bg-gradient-to-r from-white/90 via-transparent to-transparent md:from-white/70"
              aria-hidden
            />
          </div>
        </div>
      </section>

      <section className="px-4 pb-6 pt-4">
        <div className="mx-auto max-w-2xl">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
            <Input
              type="text"
              placeholder="Search articles about anxiety, attachment, communication..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-white/95 pl-12 pr-4 py-6 text-lg shadow-md backdrop-blur"
            />
          </div>
        </div>
      </section>

      <section className="px-4 pb-6">
        <div className="mx-auto grid max-w-6xl gap-4 md:grid-cols-3">
          <Card className="border-violet-100 p-5 shadow-sm">
            <h2 className="text-lg font-semibold text-neutral-900">Best relationship app</h2>
            <p className="mt-2 text-sm text-neutral-600">Evaluate core criteria and compare options quickly.</p>
            <Button variant="outline" className="mt-3 w-full" onClick={() => navigate('/best-relationship-app')}>
              Open guide
            </Button>
          </Card>
          <Card className="border-violet-100 p-5 shadow-sm">
            <h2 className="text-lg font-semibold text-neutral-900">TENSION vs Paired</h2>
            <p className="mt-2 text-sm text-neutral-600">Side-by-side comparison for communication-focused couples.</p>
            <Button variant="outline" className="mt-3 w-full" onClick={() => navigate('/compare/tension-vs-paired')}>
              Compare now
            </Button>
          </Card>
          <Card className="border-violet-100 p-5 shadow-sm">
            <h2 className="text-lg font-semibold text-neutral-900">Paired alternatives</h2>
            <p className="mt-2 text-sm text-neutral-600">Explore alternatives based on your relationship goals.</p>
            <Button variant="outline" className="mt-3 w-full" onClick={() => navigate('/alternatives/paired')}>
              View alternatives
            </Button>
          </Card>
        </div>
      </section>

      {/* Category Filter */}
      <section className="border-b border-violet-200/40 bg-white/70 px-4 py-8 backdrop-blur dark:border-violet-500/20 dark:bg-[#0c1120a6]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap gap-3 justify-center">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                !selectedCategory
                  ? 'bg-gradient-to-r from-cyan-500 to-purple-600 text-white'
                  : 'bg-white/80 text-neutral-700 hover:bg-violet-100 dark:bg-[#0f1324cc] dark:text-neutral-200 dark:hover:bg-violet-900/35'
              }`}
            >
              All Articles
            </button>
            {categories.map(category => {
              const Icon = getCategoryIcon(category);
              return (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-2 ${
                    selectedCategory === category
                      ? 'bg-gradient-to-r from-cyan-500 to-purple-600 text-white'
                      : 'bg-white/80 text-neutral-700 hover:bg-violet-100 dark:bg-[#0f1324cc] dark:text-neutral-200 dark:hover:bg-violet-900/35'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {category}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Articles */}
      {featuredPosts.length > 0 && (
        <section className="py-12 px-4">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl font-bold text-zinc-50 mb-8">Featured Articles</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {featuredPosts.map(post => {
                const Icon = getCategoryIcon(post.category);
                return (
                  <Card
                    key={post.slug}
                    className="group cursor-pointer overflow-hidden border-violet-200/70 bg-white/90 transition-all hover:-translate-y-1 hover:shadow-2xl"
                    onClick={() => navigate(`/blog/${post.slug}`)}
                  >
                    <div className="h-48 bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center">
                      <Icon className="w-20 h-20 text-white/80" />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="px-3 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full">
                          {post.category}
                        </span>
                        <span className="flex items-center gap-1 text-neutral-500 text-xs">
                          <Clock className="w-3 h-3" />
                          {post.readTime}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold text-neutral-900 mb-3 group-hover:text-purple-600 transition-colors">
                        {post.title}
                      </h3>
                      <p className="text-neutral-600 text-sm mb-4 line-clamp-3">
                        {post.description}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-neutral-500">
                          By {post.author}
                        </div>
                        <ArrowRight className="w-5 h-5 text-purple-600 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* All Articles */}
      <section className="bg-white/65 px-4 py-12 backdrop-blur dark:bg-[#090d1a99]">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-neutral-900 mb-8">All Articles</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {regularPosts.map(post => {
              const Icon = getCategoryIcon(post.category);
              return (
                <Card
                  key={post.slug}
                  className="group cursor-pointer overflow-hidden border-violet-200/70 bg-white/90 transition-all hover:-translate-y-0.5 hover:shadow-lg"
                  onClick={() => navigate(`/blog/${post.slug}`)}
                >
                  <div className="h-32 bg-gradient-to-br from-purple-300 to-pink-300 flex items-center justify-center">
                    <Icon className="w-12 h-12 text-white/80" />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full">
                        {post.category}
                      </span>
                      <span className="flex items-center gap-1 text-neutral-500 text-xs">
                        <Clock className="w-3 h-3" />
                        {post.readTime}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-neutral-900 mb-2 group-hover:text-purple-600 transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-neutral-600 text-sm mb-3 line-clamp-2">
                      {post.description}
                    </p>
                    <div className="text-xs text-neutral-500">
                      By {post.author}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-4 py-16">
        <div className="mx-auto max-w-5xl rounded-3xl border border-cyan-300/20 bg-gradient-to-r from-neutral-950 via-purple-950 to-neutral-950 p-10 text-center text-white shadow-2xl shadow-purple-900/30">
          <h2 className="text-4xl font-bold mb-6">
            Ready to Track Your Relationship Health?
          </h2>
          <p className="text-xl mb-8 text-purple-100">
            Start measuring, forecasting, and stabilizing relationship tension with TENSION.
          </p>
          <Button
            size="lg"
            onClick={() => navigate('/early-access')}
            className="bg-white text-purple-600 hover:bg-neutral-100 font-semibold px-8 py-6 text-lg"
          >
            Start Early Access
            <ArrowRight className="w-6 h-6 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-violet-500/20 bg-[#090d1ae0] py-12 px-4 text-neutral-400">
        <div className="max-w-7xl mx-auto grid md:grid-cols-5 gap-8">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-gradient-to-br from-purple-600 to-pink-600 rounded-lg flex items-center justify-center">
                <Heart className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-bold text-white">TENSION</span>
            </div>
            <p className="text-sm">
              Science-backed relationship tension monitor
            </p>
          </div>
          
          <div>
            <h4 className="text-white font-semibold mb-3">Product</h4>
            <div className="space-y-2 text-sm">
              <button onClick={() => navigate('/pricing')} className="block hover:text-white">Pricing</button>
              <button onClick={() => navigate('/about')} className="block hover:text-white">About</button>
              <button onClick={() => navigate('/faq')} className="block hover:text-white">FAQ</button>
              <button onClick={() => navigate('/home#relationship-intelligence')} className="block hover:text-white">Relationship Intelligence</button>
            </div>
          </div>
          
          <div>
            <h4 className="text-white font-semibold mb-3">Content</h4>
            <div className="space-y-2 text-sm">
              <button onClick={() => navigate('/blog')} className="block hover:text-white">Blog</button>
              <button onClick={() => navigate('/resources')} className="block hover:text-white">Resources</button>
              <button onClick={() => navigate('/guides')} className="block hover:text-white">Guides</button>
              <button onClick={() => navigate('/press')} className="block hover:text-white">Press</button>
            </div>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-3">Features</h4>
            <div className="space-y-2 text-sm">
              <button onClick={() => navigate('/features/relationship-intelligence')} className="block hover:text-white">Intelligence Suite Page</button>
              <button onClick={() => navigate('/features/conversation-micro-coach')} className="block hover:text-white">Micro-Coach Page</button>
              <button onClick={() => navigate('/relationship-intelligence/faq')} className="block hover:text-white">Intelligence FAQ</button>
              <button onClick={() => navigate('/site-map')} className="block hover:text-white">Site Map</button>
              <button onClick={() => navigate('/support')} className="block hover:text-white">Support</button>
            </div>
          </div>
          
          <div>
            <h4 className="text-white font-semibold mb-3">Business & Legal</h4>
            <div className="space-y-2 text-sm">
              <button onClick={() => navigate('/business-inquiries')} className="block hover:text-white">Investors & Business Inquiries</button>
              <button onClick={() => navigate('/contact')} className="block hover:text-white">Contact</button>
            </div>
          </div>
        </div>
        
        <div className="max-w-7xl mx-auto mt-8 pt-8 border-t border-neutral-800 text-center text-sm">
          © 2026 TENSION Labs. All rights reserved.
        </div>
      </footer>
    </div>
    </WarmMarketingBackdrop>
  );
}
